import React, { useEffect, useRef } from "react";
import {
  X, Lock, Phone, Mail, Crown, Car, CarFront, Fuel, Gauge, Hash, CalendarDays, Factory,
  Fingerprint, MapPin, Tag,
  CalendarClock, StickyNote, Receipt, Droplet, Droplets, CircleDot, Wrench,
  BatteryCharging, Sparkles, Store, Check, XCircle, CheckCircle2, MessageCircle, Eye,
} from "lucide-react";
import { Avatar, StatusBadge, VisitsBadge, PlateBadge, useDialog, DialogFrame, DialogSection as Section } from "./ui";
import { carLabel, plateLabel } from "./dashboardData";

const FONT_HEADING = { fontFamily: "'NeueMontreal', sans-serif" };

const CATEGORY_ICONS = {
  "Oil Change": Droplet,
  "Car Wash": Droplets,
  Tyres: CircleDot,
  Repair: Wrench,
  Battery: BatteryCharging,
};

// Chat lifecycle: created (locked) → accepted (unlocked) → completed | rejected
function getSteps(booking) {
  const rejected = booking.status === "Rejected";
  const accepted = booking.status === "In Chat" || booking.status === "Completed";
  return [
    { key: "created", label: "Chat created", sub: "Locked", done: true },
    rejected
      ? { key: "rejected", label: "Rejected", sub: "By provider", done: true, danger: true }
      : { key: "accepted", label: "Provider accepted", sub: "Chat unlocked", done: accepted },
    ...(rejected ? [] : [{ key: "completed", label: "Completed", sub: "Job done", done: booking.status === "Completed" }]),
  ];
}

export default function BookingDialog({ booking, onClose }) {
  const closeRef = useRef(null);
  const chatEndRef = useRef(null);

  useDialog(!!booking, onClose);
  useEffect(() => {
    if (!booking) return;
    closeRef.current?.focus();
    chatEndRef.current?.scrollIntoView({ block: "end" });
  }, [booking]);

  if (!booking) return null;

  const { customer, car, service } = booking;
  const CategoryIcon = CATEGORY_ICONS[service.category] || Wrench;
  const locked = booking.status === "Pending";
  const steps = getSteps(booking);

  return (
    <DialogFrame labelledBy="booking-dialog-title" onClose={onClose}>
        {/* Header */}
        <div
          className="relative shrink-0 px-6 pt-5 pb-5 sm:px-4 text-[#fff] overflow-hidden"
          style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 60%, #EA352B 140%)" }}
        >
          <div className="absolute -right-10 -top-16 w-48 h-48 rounded-full bg-[#EA352B] opacity-20" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs text-[#ffffffb3]">
                  <MessageCircle size={14} /> Chat booking
                </span>
                <StatusBadge status={booking.status} />
              </div>
              <h2 id="booking-dialog-title" className="m-0 mt-1.5 text-2xl sm:text-xl font-medium" style={FONT_HEADING}>
                {booking.id} · {service.category}
              </h2>
              <p className="m-0 mt-1 text-xs text-[#ffffffb3] inline-flex items-center gap-1.5">
                <Store size={13} /> {booking.shop} · {booking.provider}
              </p>
            </div>
            <button
              ref={closeRef}
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="relative z-10 shrink-0 w-9 h-9 rounded-xl bg-[#ffffff1f] hover:bg-[#ffffff33] flex items-center justify-center transition"
            >
              <X size={18} className="text-[#fff]" />
            </button>
          </div>

          {/* Stepper */}
          <ol className="relative m-0 mt-5 pl-0 list-none flex items-start">
            {steps.map((st, i) => (
              <li key={st.key} className="flex-1 flex items-start min-w-0">
                <div className="flex flex-col items-center text-center min-w-0 flex-1">
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                      st.danger
                        ? "bg-[#EA352B] border-[#EA352B]"
                        : st.done
                        ? "bg-[#16A34A] border-[#16A34A]"
                        : "bg-transparent border-[#ffffff4d]"
                    }`}
                  >
                    {st.danger ? <X size={15} /> : st.done ? <Check size={15} /> : <span className="w-2 h-2 rounded-full bg-[#ffffff66]" />}
                  </span>
                  <span className={`mt-1.5 text-[12px] font-medium truncate max-w-full ${st.done ? "text-[#fff]" : "text-[#ffffff80]"}`}>
                    {st.label}
                  </span>
                  <span className="text-[10px] text-[#ffffff80]">{booking.timeline[i]?.time || st.sub}</span>
                </div>
                {i < steps.length - 1 && (
                  <span className={`mt-4 h-0.5 flex-1 rounded-full ${steps[i + 1].done ? "bg-[#ffffffb3]" : "bg-[#ffffff33]"}`} />
                )}
              </li>
            ))}
          </ol>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-[1.05fr_1fr] md:grid-cols-1 gap-5 p-6 sm:p-4">
          {/* Left: request details */}
          <div className="space-y-4 min-w-0">
            <Section title="Customer">
              <div className="flex items-center gap-3">
                <Avatar name={customer.name} size={48} />
                <div className="min-w-0">
                  <p className="m-0 text-base font-semibold text-[#111] flex items-center gap-1.5 flex-wrap">
                    {customer.name}
                    {customer.subscribed && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#FDECEA] text-[#EA352B] text-[10px] font-semibold px-2 py-0.5">
                        <Crown size={10} /> Subscriber
                      </span>
                    )}
                  </p>
                  <p className="m-0 text-xs text-[#9CA3AF]">Member since {customer.memberSince}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 mt-4">
                <InfoLine Icon={Phone} value={customer.phone} />
                <InfoLine Icon={Mail} value={customer.email} />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-xl bg-[#F7F7F9] px-3 py-2.5">
                <span className="text-xs text-[#6B7280]">Visits to {booking.shop}</span>
                <VisitsBadge visits={booking.visits} />
              </div>
            </Section>

            <Section title="Car details">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-2xl bg-[#1B1B22] flex items-center justify-center shrink-0">
                  <Car size={22} className="text-[#fff]" />
                </span>
                <div className="min-w-0">
                  <p className="m-0 text-base font-semibold text-[#111]">{carLabel(car)}</p>
                  <p className="m-0 text-xs text-[#9CA3AF]">Selected from customer’s garage</p>
                </div>
              </div>

              <h4 className="m-0 mt-5 mb-2 text-[11px] font-semibold text-[#6B7280]">Basic vehicle info</h4>
              <div className="grid grid-cols-3 sm:grid-cols-2 gap-2">
                <Spec Icon={CarFront} label="Vehicle type" value={car.vehicleType} />
                <Spec Icon={CalendarDays} label="Year" value={car.vehicleYear} />
                <Spec Icon={Factory} label="Make" value={car.vehicleMake} />
                <Spec Icon={Car} label="Model" value={car.vehicleModel} />
                <Spec Icon={Fuel} label="Fuel type" value={car.fuelType} />
                <Spec Icon={Gauge} label="Odometer" value={car.currentOdometerReading != null ? `${Number(car.currentOdometerReading).toLocaleString()} km` : ""} />
              </div>
              <div className="mt-2">
                <Spec Icon={Fingerprint} label="Vehicle identification (VIN)" value={car.vehicleIdentificaionNumber} mono />
              </div>

              <h4 className="m-0 mt-5 mb-2 text-[11px] font-semibold text-[#6B7280]">Plate information</h4>
              <PlateBadge car={car} />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Spec Icon={MapPin} label="Plate source" value={car.plateSource} />
                <Spec Icon={Tag} label="Plate category" value={car.plateCategory} />
                <Spec Icon={Hash} label="Plate code" value={car.plateCode} />
                <Spec Icon={Hash} label="Plate number" value={car.plate} />
              </div>
            </Section>

            <Section title="Requested service">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-2xl bg-[#FDECEA] flex items-center justify-center shrink-0">
                    <CategoryIcon size={22} className="text-[#EA352B]" />
                  </span>
                  <div>
                    <p className="m-0 text-[11px] uppercase tracking-wider text-[#9CA3AF]">Category</p>
                    <p className="m-0 text-base font-semibold text-[#111]">{service.category}</p>
                  </div>
                </div>
                {service.estimate && (
                  <div className="text-right">
                    <p className="m-0 text-[11px] uppercase tracking-wider text-[#9CA3AF]">Estimate</p>
                    <p className="m-0 text-base font-semibold text-[#111]">{service.estimate}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-2xl border border-[#EEEEF1] divide-y divide-[#EEEEF1] overflow-hidden">
                {service.options.map((o) => (
                  <div key={o.label} className="flex items-center justify-between gap-3 px-3.5 py-2.5 bg-[#fff]">
                    <span className="text-xs text-[#6B7280]">{o.label}</span>
                    <span className="text-sm font-medium text-[#111] text-right">{o.value}</span>
                  </div>
                ))}
              </div>

              {(service.preferredTime || service.estimate) && (
                <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 mt-3">
                  {service.preferredTime && <InfoLine Icon={CalendarClock} value={service.preferredTime} />}
                  {service.estimate && <InfoLine Icon={Receipt} value={`Estimate ${service.estimate}`} />}
                </div>
              )}
              {service.notes && (
                <div className="mt-3 flex gap-2 rounded-xl bg-[#FFF8E6] px-3 py-2.5">
                  <StickyNote size={15} className="text-[#B45309] shrink-0 mt-0.5" />
                  <p className="m-0 text-xs text-[#78350F]">{service.notes}</p>
                </div>
              )}
            </Section>
          </div>

          {/* Right: chat */}
          <div className="min-w-0 flex flex-col rounded-3xl bg-[#fff] border border-[#EEEEF1] overflow-hidden min-h-[460px] md:min-h-[420px]">
            <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-[#EEEEF1]">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar name={booking.shop} />
                <div className="min-w-0">
                  <p className="m-0 text-sm font-semibold text-[#111] truncate">{customer.name} ↔ {booking.shop}</p>
                  <p className="m-0 text-[11px] text-[#9CA3AF]">
                    {booking.messagesCount} messages
                  </p>
                </div>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[#6B7280] text-[10px] font-semibold px-2 py-1">
                <Eye size={11} /> Admin view
              </span>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFB]">
              <RequestCard booking={booking} CategoryIcon={CategoryIcon} />
              {booking.chat.map((m, i) => (
                <ChatMessage key={i} message={m} />
              ))}
              <div ref={chatEndRef} />
            </div>

            <ChatFooter booking={booking} locked={locked} />
          </div>
        </div>
    </DialogFrame>
  );
}

function InfoLine({ Icon, value }) {
  return (
    <span className="flex items-center gap-2 rounded-xl bg-[#F7F7F9] px-3 py-2 text-xs text-[#374151] min-w-0">
      <Icon size={14} className="text-[#EA352B] shrink-0" />
      <span className="truncate">{value || "Not provided"}</span>
    </span>
  );
}

function Spec({ Icon, label, value, mono }) {
  return (
    <div className="rounded-xl bg-[#F7F7F9] px-3 py-2.5 min-w-0">
      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#9CA3AF]">
        <Icon size={11} className="shrink-0" /> <span className="truncate">{label}</span>
      </span>
      <span
        className={`block mt-1 text-[13px] font-medium truncate ${value ? "text-[#111]" : "text-[#C0C4CC]"} ${mono ? "font-mono tracking-wide" : ""}`}
        title={value ? String(value) : undefined}
      >
        {value || "Not provided"}
      </span>
    </div>
  );
}


// The first message in every chat: the booking request the app sends automatically.
function RequestCard({ booking, CategoryIcon }) {
  const { customer, car, service } = booking;
  return (
    <div className="flex justify-start">
      <div className="max-w-[88%] rounded-2xl rounded-tl-md border border-[#F6C9C5] bg-[#fff] overflow-hidden shadow-sm">
        <div className="flex items-center gap-2 px-3.5 py-2 bg-[#FDECEA] text-[#EA352B] text-[11px] font-semibold">
          <Sparkles size={13} /> Booking request · sent automatically
        </div>
        <div className="px-3.5 py-3 space-y-2">
          <p className="m-0 text-[13px] text-[#111]">
            <b>{customer.name}</b> requested <b>{service.category}</b>
          </p>
          <p className="m-0 flex items-center gap-1.5 text-xs text-[#374151]">
            <Car size={13} className="text-[#9CA3AF]" /> {carLabel(car)} · {plateLabel(car)}
          </p>
          <p className="m-0 flex items-start gap-1.5 text-xs text-[#374151]">
            <CategoryIcon size={13} className="text-[#9CA3AF] mt-0.5 shrink-0" />
            <span>{service.options.map((o) => o.value).join(" · ")}</span>
          </p>
          {service.preferredTime && (
            <p className="m-0 flex items-center gap-1.5 text-xs text-[#374151]">
              <CalendarClock size={13} className="text-[#9CA3AF]" /> {service.preferredTime}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ message }) {
  if (message.from === "system") {
    return (
      <div className="flex justify-center">
        <span className="max-w-[90%] text-center rounded-full bg-[#EEEEF1] text-[#6B7280] text-[11px] px-3 py-1">
          {message.text} · {message.time}
        </span>
      </div>
    );
  }
  const isProvider = message.from === "provider";
  return (
    <div className={`flex ${isProvider ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug shadow-sm ${
          isProvider ? "bg-[#EA352B] text-[#fff] rounded-tr-md" : "bg-[#fff] text-[#111] border border-[#EEEEF1] rounded-tl-md"
        }`}
      >
        {message.picture && (
          <a href={message.picture} target="_blank" rel="noreferrer">
            <img src={message.picture} alt="Attachment" className="block max-w-full max-h-48 rounded-xl mb-1.5 object-cover" />
          </a>
        )}
        {message.text && <p className="m-0">{message.text}</p>}
        <p className={`m-0 mt-1 text-[10px] text-right ${isProvider ? "text-[#ffffffb3]" : "text-[#9CA3AF]"}`}>
          {isProvider ? "Provider" : "Customer"} · {message.time}
        </p>
      </div>
    </div>
  );
}

function ChatFooter({ booking, locked }) {
  if (locked) {
    return (
      <div className="shrink-0 border-t border-[#EEEEF1] bg-[#FFFBEB] px-4 py-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#FEF3C7] flex items-center justify-center shrink-0">
          <Lock size={18} className="text-[#B45309]" />
        </span>
        <div>
          <p className="m-0 text-sm font-semibold text-[#78350F]">Chat locked</p>
          <p className="m-0 text-xs text-[#92400E]">
            Waiting for {booking.shop} to accept. The customer can’t message until then.
          </p>
        </div>
      </div>
    );
  }
  if (booking.status === "Rejected") {
    return (
      <div className="shrink-0 border-t border-[#EEEEF1] bg-[#FFF5F4] px-4 py-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#FDECEA] flex items-center justify-center shrink-0">
          <XCircle size={18} className="text-[#EA352B]" />
        </span>
        <div>
          <p className="m-0 text-sm font-semibold text-[#991B1B]">Rejected by provider</p>
          <p className="m-0 text-xs text-[#B91C1C]">{booking.rejectReason || "No reason given."}</p>
        </div>
      </div>
    );
  }
  if (booking.status === "Completed") {
    return (
      <div className="shrink-0 border-t border-[#EEEEF1] bg-[#F0FDF4] px-4 py-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#DCFCE7] flex items-center justify-center shrink-0">
          <CheckCircle2 size={18} className="text-[#16A34A]" />
        </span>
        <div>
          <p className="m-0 text-sm font-semibold text-[#14532D]">Job completed</p>
          <p className="m-0 text-xs text-[#166534]">{booking.shop} marked this booking as done. Chat is closed.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="shrink-0 border-t border-[#EEEEF1] bg-[#EFF6FF] px-4 py-3 flex items-center gap-2 text-xs text-[#1E40AF]">
      <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
      Chat is active between customer and provider · read-only for admin
    </div>
  );
}
